import { DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { UserService } from 'app/core/user/user.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { FilterParams, FiltersService } from './filters.service';
import { SiteService, SitesMap } from 'app/core/sites/site.service';

export interface FilterGenericType {
    id: string;
    name: string;
}

@Component({
    selector: 'filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'agriFilters',
    animations: fuseAnimations,
})
export class FiltersComponent implements OnInit, OnDestroy {
    /**
     * Objects
     */
    @Output() currentFilter = new EventEmitter<FilterParams>();
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    siteList: FilterGenericType[] = [];
    combinedCodes: any[] = [];

    data = [];
    selDateTo: Date;
    selDateFrom: Date;
    updates: FilterParams;
    currentFilterValues: FilterParams;
    // site$: Observable<string[]>;
    site$: Observable<SitesMap[]>;
    //if user has no ADMIN role, should only see certain sites
    // userSite: string;
    // currentCfmsSite: string[];
    userSite: SitesMap;
    //selected FT site object from dropdown
    selectedSite: any[];

    //sitesMapping to find the corresponding values
    sitesMap: any[];

    currentCfmsSite: any[] = [];
    currentDateFrom: Date;
    currentDateTo: Date;
    userRoles: string[] = [];
    filterView$: Observable<{ site: boolean; from: boolean; to: boolean }>;

    /**
     * Constructor
     * @param router
     * @param _filtersService
     */
    constructor(
        private router: Router,
        private _filtersService: FiltersService,
        private _userService: UserService,
        private _siteService: SiteService
    ) {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.getFilter();
    }

    // -------------------------------------------------------------------------------------------------------
    // Lifecycle hooks
    // -------------------------------------------------------------------------------------------------------

    /**
     * OnInit
     */
    ngOnInit(): void {
        // console.log('[Filters Component] Init')

        // Get user to check role in order to display proper site list
        this.getUser();

        // Get filter flag to update the view
        this.filterView$ = this._filtersService.inUseFilters$;
    }

    /**
     * OnDestroy
     */
    ngOnDestroy(): void {
        // console.log('[FILTERS COMPONENT] unsubscribe');
        this._filtersService.unSubscribe();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -------------------------------------------------------------------------------------------------------
    // Public Methods
    // -------------------------------------------------------------------------------------------------------

    filter() {
        this.selDateFrom = undefined;
        this.selDateTo = undefined;
        if (this.currentDateFrom != null) {
            this.selDateFrom = this.currentDateFrom;
        }
        if (this.currentDateTo != null) {
            this.selDateTo = this.currentDateTo;
        }

        // this.updates = {site: this.combinedCodes, from: this.selDateFrom, to: this.selDateTo};
        this.updates = {
            site: this.combinedCodes,
            cfms_site: this.currentCfmsSite,
            from: this.selDateFrom,
            to: this.selDateTo,
        };
        this.currentFilter.emit(this.updates);
        this._filtersService.updateFilter(this.updates);
    }

    clearFilter() {
        this.selDateFrom = undefined;
        this.selDateTo = undefined;
        this.selectedSite = [];
        this.combinedCodes = undefined;
        this.currentCfmsSite = undefined;
        this.currentDateFrom = undefined;
        this.currentDateTo = undefined;
        this.currentFilterValues = {
            site: this.combinedCodes,
            cfms_site: this.currentCfmsSite,
            from: this.currentDateFrom,
            to: this.currentDateTo,
        };
        this.updates = {
            site: this.combinedCodes,
            cfms_site: this.currentCfmsSite,
            from: this.currentDateFrom,
            to: this.currentDateTo,
        };
        this.currentFilter.emit(this.currentFilterValues);
    }

    // -------------------------------------------------------------------------------------------------------
    // Private Methods
    // -------------------------------------------------------------------------------------------------------

    private getUser(): void {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    if (!response || !response.roles) {
                        console.error('Invalid user response:', response);
                        return;
                    }

                    this.userRoles = response.roles;

                    if (
                        this.userRoles?.includes('ROLE_ADMIN') ||
                        this.userRoles?.includes('ROLE_GENERAL_MANAGER')
                    ) {
                        this.site$ = this._siteService.sites$;
                    } else {
                        this.site$ = this._userService.userSite$;
                        // this.sitesMap = this._userService.userSite;
                    }
                },
                error: (er) => {
                    console.log(er);
                },
            });
    }

    private getFilter(): void {
        this.currentDateFrom = undefined;
        this.currentDateTo = undefined;
        this._filtersService.filter$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    if (response.from) {
                        this.currentDateFrom = response.from;
                    }
                    if (response.to) {
                        this.currentDateTo = response.to;
                    }
                    if (response.site && response.site.length != 0) {
                        if (typeof response.site != 'string') {
                            this.combinedCodes = response.site;
                        } else {
                            this.combinedCodes = [response.site];
                        }
                    }
                    if (response.cfms_site && response.cfms_site.length != 0) {
                        if (typeof response.cfms_code != 'string') {
                            this.selectedSite = response.cfms_site;
                        } else {
                            this.selectedSite = [response.cfms_site];
                        }
                    }

                    this.currentFilterValues = {
                        site: this.combinedCodes,
                        cfms_site: this.selectedSite,
                        from: this.currentDateFrom,
                        to: this.currentDateTo,
                    };
                },
                error: (er) => {
                    console.log(er);
                },
            });
    }

    /*This method is called each time a user selects filters
  so that the site_codes are sent to the backend
  */
    combineCodes() {
        // this.combinedCodes = this.sitesMap.filter(site => this.selectedSite.includes(site.cfms_code)).flatMap(site=>site.codes);
        this.combinedCodes = this.selectedSite.flatMap((site) => site.codes);
        this.currentCfmsSite = this.selectedSite.flatMap(
            (site) => site.cfms_code
        );
    }

    trackByFn(index, item) {
        return item.cfms_code; // Use the 'id' property as the unique identifier
    }
}
